import { useEffect, useState } from "react";
import {
  Button,
  ConfirmDialog,
  CONCEPT_ICONS,
  PocketRow,
  PocketSection,
  PocketStatement,
  TextInput,
} from "@/components/ui";
import { formatDate, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsConnectionsMessages } from "@/i18n/messages/settings-connections";
import {
  COMPOSIO_UI_ENABLED,
  connectionStatusLabel,
  toolkitDisplayName,
} from "@/lib/composio";
import { selectToolkit, useIntegrationStore } from "@/stores/integration-store";
import { useSettingsNav } from "./settings-navigation";

export function ConnectionAppPage({ app }: { app: string }) {
  const t = useT(settingsConnectionsMessages);
  const nav = useSettingsNav();
  const connection = useIntegrationStore((state) => state.connections.get(app));
  const pending = useIntegrationStore((state) => state.pendingAuth.has(app));
  const toolkit = useIntegrationStore((state) => selectToolkit(state, app));
  const actions = useIntegrationStore((state) => state.actions);
  const error = useIntegrationStore((state) => state.error);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (!COMPOSIO_UI_ENABLED) return;
    void actions.refreshConnections();
    void actions.resolveToolkit(app);
  }, [actions, app]);
  if (!COMPOSIO_UI_ENABLED || app === "composio")
    return <PocketStatement size="panel" title={t("appsUnavailable")} />;
  const name = toolkitDisplayName(app, toolkit);
  const status = pending ? "initiated" : connection?.status;
  const statusLabel = status
    ? connectionStatusLabel(status)
    : t("appNotConnected");
  return (
    <div data-testid="settings-page-connection-app">
      <PocketStatement size="panel" title={name} line={statusLabel} />
      <PocketSection>
        <PocketRow
          testId="connection-app-status"
          title={t("status")}
          secondLine={pending ? t("signInFinishBrowser") : undefined}
          trailing={
            <span className="block max-w-40 truncate" title={statusLabel}>
              {statusLabel}
            </span>
          }
        />
        {connection?.connected_at && (
          <PocketRow
            testId="connection-app-since"
            title={t("connectedSince")}
            trailing={formatDate(connection.connected_at)}
          />
        )}
        {status !== "active" && status !== "initiated" && (
          <PocketRow
            testId="connection-app-reconnect"
            title={connection ? t("reconnect") : t("connect")}
            secondLine={t("opensBrowserToSignIn")}
            onSelect={() => void actions.initiateConnection(app)}
          />
        )}
      </PocketSection>
      {(connection || pending) && (
        <div className="mt-3 px-2">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setConfirm(true)}
            data-testid="connection-app-disconnect"
          >
            {t("disconnectApp", { name })}
          </Button>
        </div>
      )}
      {error && (
        <p role="alert" className="px-2 text-appkit-small text-danger">
          {error}
        </p>
      )}
      {confirm && (
        <ConfirmDialog
          title={t("disconnectAppQuestion", { name })}
          description={t("disconnectAppDescription")}
          confirmLabel={t("disconnect")}
          variant="danger"
          data-testid="connection-app-confirm"
          onCancel={() => setConfirm(false)}
          onConfirm={() => {
            void actions.disconnect(app).then(() => {
              setConfirm(false);
              if (!useIntegrationStore.getState().connections.has(app))
                nav.back();
            });
          }}
        />
      )}
    </div>
  );
}

export function AddConnectionAppPage() {
  const t = useT(settingsConnectionsMessages);
  const tCommon = useT(commonMessages);
  const [query, setQuery] = useState("");
  const toolkits = useIntegrationStore((state) => state.toolkits);
  const connections = useIntegrationStore((state) => state.connections);
  const pending = useIntegrationStore((state) => state.pendingAuth);
  const loading = useIntegrationStore((state) => state.loading);
  const hasMore = useIntegrationStore((state) => state.toolkitsHasMore);
  const loadingMore = useIntegrationStore((state) => state.toolkitsLoadingMore);
  const error = useIntegrationStore((state) => state.error);
  const actions = useIntegrationStore((state) => state.actions);
  useEffect(() => {
    if (!COMPOSIO_UI_ENABLED) return;
    const timer = window.setTimeout(
      () => void actions.loadToolkits(query.trim() || undefined),
      250,
    );
    return () => window.clearTimeout(timer);
  }, [actions, query]);
  if (!COMPOSIO_UI_ENABLED)
    return <PocketStatement size="panel" title={t("appsUnavailable")} />;
  const rows = toolkits.filter((item) => item.name !== "composio");
  return (
    <div data-testid="settings-page-add-app">
      <TextInput
        shape="capsule"
        aria-label={t("searchApps")}
        placeholder={t("searchAppsPlaceholder")}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        data-testid="connections-app-search"
      />
      <p
        className="px-2 pt-2 text-appkit-small text-muted"
        data-testid="connections-catalog-hint"
      >
        {t("connectOpensBrowser")}
      </p>
      {error && (
        <p role="alert" className="px-2 text-appkit-small text-danger">
          {error}
        </p>
      )}
      <PocketSection>
        {rows.map((toolkit) => {
          const status = connections.get(toolkit.name)?.status;
          const connecting =
            pending.has(toolkit.name) || status === "initiated";
          return (
            <PocketRow
              key={toolkit.name}
              testId={`connections-catalog-${toolkit.name}`}
              title={toolkit.display_name || toolkit.name}
              // A catalog's descriptions: two lines each, where settings wrap.
              secondLine={
                <span className="line-clamp-2">{toolkit.description}</span>
              }
              leading={<CONCEPT_ICONS.app className="h-4 w-4 text-muted" />}
              trailing={
                status === "active" ? (
                  t("connected")
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={connecting}
                    data-testid={`connections-connect-${toolkit.name}`}
                    onClick={() =>
                      void actions.initiateConnection(toolkit.name)
                    }
                  >
                    {connecting ? t("connecting") : t("connect")}
                  </Button>
                )
              }
            />
          );
        })}
      </PocketSection>
      {loading && (
        <p role="status" className="px-2 text-appkit-small text-muted">
          {t("loadingApps")}
        </p>
      )}
      {!loading && !rows.length && (
        <PocketStatement size="empty" title={t("noAppsFound")} />
      )}
      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          disabled={loadingMore}
          data-testid="connections-load-more"
          onClick={() => void actions.loadMoreToolkits()}
        >
          {loadingMore ? tCommon("loading") : t("loadMore")}
        </Button>
      )}
    </div>
  );
}
